import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCourse } from '../contexts/CourseContext';
import { SchoolOutlined, AddOutlined } from '../icons';
import Dropdown from './Dropdown';
import PlansModal from './PlansModal';
import './CourseSwitcher.css';

const ADD_COURSE_VALUE = '__add_course__';

// Lista só os concursos "registrados" do usuário (personalizados ou já com
// plano de estudos), não o catálogo inteiro — pra adicionar um concurso novo
// (existente ou personalizado), "Adicionar concurso" leva pra
// /escolher-concurso, que já esconde os que o usuário já tem.
function CourseSwitcher({ inline = false, disabled = false }) {
  const navigate = useNavigate();
  const courseCtx = useCourse();
  const [showPlansModal, setShowPlansModal] = useState(false);

  if (!courseCtx) return null;

  const { registeredCourses, activeCourseId, setActiveCourseId, loadingCourses, hasFreePlan } = courseCtx;

  if (loadingCourses || registeredCourses.length === 0) return null;

  const wrapperClassName = `course-switcher ${inline ? 'course-switcher--inline' : 'course-switcher--fixed'}`;

  // Plano grátis só pode ter 1 concurso registrado (ver POST /myCourses e
  // POST /studyPlan/generate no backend) — em vez de deixar o usuário
  // navegar pra /escolher-concurso pra só então descobrir que está bloqueado,
  // já mostra o convite de upgrade aqui.
  const goToAddCourse = () => {
    if (hasFreePlan && registeredCourses.length >= 1) {
      setShowPlansModal(true);
      return;
    }
    navigate('/escolher-concurso');
  };

  const handleDropdownChange = (value) => {
    if (value === ADD_COURSE_VALUE) {
      goToAddCourse();
      return;
    }
    setActiveCourseId(value);
  };

  const addOption = {
    value: ADD_COURSE_VALUE,
    label: 'Adicionar concurso',
    icon: <AddOutlined style={{ fontSize: 16 }} />,
  };

  const plansModal = showPlansModal && (
    <PlansModal
      trackingPrefix="course_limit_wall"
      heroTitle="Seu plano grátis permite 1 concurso"
      heroSubtitle="Libere acesso completo para estudar para quantos concursos quiser, com simulados e materiais de cada um."
      onClose={() => setShowPlansModal(false)}
    />
  );

  if (registeredCourses.length === 1) {
    return (
      <>
        <div className={`${wrapperClassName} course-switcher-single`}>
          <SchoolOutlined style={{ fontSize: 18 }} />
          <span>{registeredCourses[0].name}</span>
          {!disabled && (
            <button
              type="button"
              className="course-switcher-add-btn"
              onClick={goToAddCourse}
              aria-label="Adicionar concurso"
              title="Adicionar concurso"
            >
              <AddOutlined style={{ fontSize: 16 }} />
            </button>
          )}
        </div>
        {plansModal}
      </>
    );
  }

  return (
    <>
      <div className={wrapperClassName}>
        <Dropdown
          value={activeCourseId}
          onChange={handleDropdownChange}
          options={[
            ...registeredCourses.map((course) => ({
              value: String(course.id),
              label: course.name,
              image: course.image_url,
              icon: <SchoolOutlined style={{ fontSize: 16 }} />,
            })),
            addOption,
          ]}
          headerLabel="Selecione um concurso"
          icon={<SchoolOutlined style={{ fontSize: 16 }} />}
          placeholder="Selecione um concurso"
          disabled={disabled}
          ariaLabel="Concurso selecionado"
          className="course-switcher-dropdown"
          triggerClassName="course-switcher-trigger"
        />
      </div>
      {plansModal}
    </>
  );
}

export default CourseSwitcher;
